/** Shared task/event timeframe filtering used by the CRM dashboards. */

export const convertToDate = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue?.toDate) {
    return dateValue.toDate();
  }
  if (dateValue instanceof Date) {
    return dateValue;
  }
  return new Date(dateValue);
};

export const filterTasksByTimeframe = (tasks, timeframe) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const endOfWeek = new Date(today);
  endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
  endOfWeek.setHours(23, 59, 59, 999);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);

  return tasks
    .filter((task) => {
      if (!task.dueDate) {
        return true;
      }
      const dueDate = convertToDate(task.dueDate);
      const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());

      switch (timeframe) {
        case 'Today':
          return dueDateOnly <= tomorrow;
        case 'This Week':
          return dueDateOnly <= endOfWeek;
        case 'This Month':
          return dueDateOnly <= endOfMonth;
        default:
          return true;
      }
    })
    .sort((a, b) => {
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1;
      }
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      const aDate = convertToDate(a.dueDate);
      const bDate = convertToDate(b.dueDate);
      return aDate - bDate;
    });
};

export const filterEventsByTimeframe = (events, timeframe) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const endOfWeek = new Date(today);
  endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
  endOfWeek.setHours(23, 59, 59, 999);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);

  return events
    .filter((event) => {
      const eventTimeRaw = event.startTime || event.start;
      if (!eventTimeRaw) return false;
      const eventDate = convertToDate(eventTimeRaw);
      if (!eventDate) return false;

      switch (timeframe) {
        case 'Today':
          return eventDate >= today && eventDate < tomorrow;
        case 'This Week':
          return eventDate >= today && eventDate <= endOfWeek;
        case 'This Month':
          return eventDate >= today && eventDate <= endOfMonth;
        default:
          return true;
      }
    })
    .sort((a, b) => {
      const aTime = convertToDate(a.startTime || a.start);
      const bTime = convertToDate(b.startTime || b.start);
      if (!aTime && !bTime) return 0;
      if (!aTime) return 1;
      if (!bTime) return -1;
      return aTime - bTime;
    });
};

/**
 * Bucket a task by due-date urgency for color-coded emphasis.
 * @returns {'overdue'|'today'|'week'|'none'}
 */
export const getTaskUrgency = (task) => {
  if (!task?.dueDate) return 'none';
  const due = convertToDate(task.dueDate);
  if (!due) return 'none';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDateOnly = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const endOfWeek = new Date(today);
  endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
  endOfWeek.setHours(23, 59, 59, 999);

  if (dueDateOnly < today) return 'overdue';
  if (dueDateOnly.getTime() === today.getTime()) return 'today';
  if (dueDateOnly <= endOfWeek) return 'week';
  return 'none';
};
